import React, { useActionState, useState } from "react"
import Form from "next/form"
import Link from "next/link"
import { redirect } from "next/navigation"
import { addProductToWishlistAction, createWishlistAction } from "@/actions/wishlistActions"
import { useUbPDPState } from "@/store/ubPDPState"
import { ClipboardList, LoaderCircle } from "lucide-react"
import { useForm } from "react-hook-form"

import { hasEntries } from "@/lib/helper"
import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { zodResolver } from "@/lib/polyfill/zodResolver"
import { Wishlist } from "@/lib/storefront/account/wishlist-service"
import { ReducedProductData } from "@/lib/storefront/product/product"
import {
  Button,
  buttonVariants,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/components"
import { cn } from "@/components/ui/helpers/utils"
import { UbProductAddToWishlistActionStoryblok } from "@/components/storyblok/component-types"
import { CreateWishlistSchema, safeParse } from "@/components/wishlist/CreateWishlist"

interface ProductDetailBuyboxAddToWishlistActionClientProps {
  blok?: UbProductAddToWishlistActionStoryblok
  wishlists: Wishlist[]
  isAnonymous: boolean
  sbLocale: string
  article: ReducedProductData | undefined
}

export function ProductDetailBuyboxAddToWishlistActionClient(props: ProductDetailBuyboxAddToWishlistActionClientProps) {
  const { buyboxQuantity } = useUbPDPState()

  const [createDialogIsOpen, setCreateDialogIsOpen] = useState(false)
  const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false)
  const [confirmDialogMessage, setConfirmDialogMessage] = useState(props.blok?.confirm_dialog_message)

  const [wishlistName, setWishlistName] = useState("")
  const [wishlistId, setWishlistId] = useState("")
  const [wishlists, setWishlists] = useState(props.wishlists)

  const resolver = zodResolver(CreateWishlistSchema)

  async function formAction(initialState: any, formData: FormData) {
    const values = {
      name: formData.get("name") as string,
    }
    let wishlistName = formData.get("wishlistName") as string
    let errors = {}
    if (!wishlistName || wishlistName === "") {
      wishlistName = values.name
      errors = safeParse(values)
    }
    if (!hasEntries(errors)) {
      const wishlistId = wishlists.find((wishlist) => wishlist.title === wishlistName)?.id
      const promise = wishlistId ? Promise.resolve(wishlistId) : createWishlistAction(wishlistName)
      promise
        .then((wishlistId) => {
          addProductToWishlistAction(wishlistId, props.article?.id, buyboxQuantity)
          return wishlistId
        })
        .then((wishlistId) => {
          setConfirmDialogMessage(
            confirmDialogMessage
              ? JSON.parse(JSON.stringify(confirmDialogMessage).replace("{0}", wishlistName))
              : confirmDialogMessage
          )
          setWishlistName(wishlistName)
          setWishlistId(wishlistId)
          if (!wishlists.find((wishlist) => wishlist.title === wishlistName && wishlist.id === wishlistId)) {
            setWishlists([{ title: wishlistName, id: wishlistId }, ...wishlists])
          }
          setCreateDialogIsOpen(false)
          setConfirmDialogIsOpen(true)
        })
        .catch((err) => {
          console.error(err, wishlistName)
          errors["name"] = {}
        })
    }
    return {
      values,
      errors,
    }
  }

  const [state, action, isPending] = useActionState(formAction, {})

  const { formState, register, reset } = useForm({
    errors: state.errors,
    mode: "onChange",
    resolver,
  })

  function onCreateDialogOpenChange(open: boolean) {
    reset()
    if (open && props.isAnonymous) {
      const wishlistRedirectUrl = getLinkWithPrefixFromURLAndLocale("/auth/signin", props.sbLocale)
      redirect(`${wishlistRedirectUrl}?loginRedirectUrl=${encodeURIComponent(window.location.href)}`)
    }
    setCreateDialogIsOpen(open)
  }

  return (
    props.blok?.enabled && (
      <>
        <Dialog open={createDialogIsOpen} onOpenChange={onCreateDialogOpenChange}>
          <DialogTrigger className={cn(buttonVariants({ variant: "outline_secondary", size: "defaultNoGrow" }))}>
            <div className={"flex w-full flex-row items-center justify-between gap-1"}>
              <p>{props.blok?.label}</p>
              <ClipboardList />
            </div>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{props.blok.create_dialog_title}</DialogTitle>
            </DialogHeader>
            <Form className={"flex flex-col gap-4"} action={action}>
              {wishlists.length > 0 ? (
                <RadioGroup name={"wishlistName"} defaultValue={""}>
                  {wishlists.map((wishlist) => (
                    <div className="flex flex-row items-center gap-2">
                      <RadioGroupItem id={wishlist.id} value={wishlist.title} />
                      <label htmlFor={wishlist.id}>{wishlist.title}</label>
                    </div>
                  ))}
                  <div className="flex flex-row items-center gap-2">
                    <RadioGroupItem id="new-wishlist" value="" />
                    <label htmlFor="new-wishlist">
                      <Input
                        type="text"
                        placeholder={props.blok.create_dialog_name_placeholder}
                        {...register("name")}
                      />
                      {formState.errors.name ? (
                        <p className={"text-sm text-primary"} role="alert">
                          {formState.errors.name.type === "too_big"
                            ? props.blok.create_dialog_name_too_big
                            : props.blok.create_dialog_name_too_small}
                        </p>
                      ) : null}
                    </label>
                  </div>
                </RadioGroup>
              ) : (
                <>
                  <Input type="text" placeholder={props.blok.create_dialog_name_placeholder} {...register("name")} />
                  {formState.errors.name ? (
                    <p className={"text-sm text-primary"} role="alert">
                      {formState.errors.name.type === "too_big"
                        ? props.blok.create_dialog_name_too_big
                        : props.blok.create_dialog_name_too_small}
                    </p>
                  ) : null}
                </>
              )}
              <Button className={cn(buttonVariants({ size: "default" }))} disabled={isPending}>
                {!isPending ? (
                  <span>{props.blok.create_dialog_submit}</span>
                ) : (
                  <LoaderCircle className={"animate-spin"} />
                )}
              </Button>
            </Form>
          </DialogContent>
        </Dialog>
        <Dialog open={confirmDialogIsOpen} onOpenChange={setConfirmDialogIsOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{props.blok.confirm_dialog_title}</DialogTitle>
              <DialogDescription>
                {confirmDialogMessage}
                <br />
                {props.blok.confirm_dialog_link_label && wishlistName && wishlistId && (
                  <>
                    {props.article?.title} {props.blok.confirm_dialog_link_label}{" "}
                    <Link
                      href={getLinkWithPrefixFromURLAndLocale(`/account/wishlist/${wishlistId}`, props.sbLocale)}
                      prefetch={false}
                      className={"underline"}
                    >
                      {wishlistName}
                    </Link>
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogClose className={cn(buttonVariants({ size: "default" }))}>
              {props.blok.confirm_dialog_submit}
            </DialogClose>
          </DialogContent>
        </Dialog>
      </>
    )
  )
}
